<template>
  <div class="filters-list">
    <FilterItem
      v-for="filter in filterList"
      :key="filter.filterKey"
      :is-toggle="filter.isToggle"
      :classes="filter.customClasses"
      :selected-classes="filter.customSelectedClasses"
      v-bind="filter"
      @search-updated="filter.emits['search-updated']"
      @filters-updated="filter.emits['filters-updated']"
    >
      <template v-if="filter.icon" #prefix="{ isOpen, isSelected }">
        <component
          :is="
            filter.iconActive && (isOpen || isSelected)
              ? filter.iconActive
              : filter.icon
          "
          :class="filter.iconClass"
        />
      </template>
      <template v-if="filter.arrowIcon" #suffix>
        <IconChevronDown class="ml-1 mt-1 anchor font-normal w-3 h-3" />
      </template>
      <template
        v-if="filter.contentComponent"
        #content="{ modelValue, updated, confirmed }"
      >
        <component
          :is="filter.contentComponent"
          :model-value="modelValue"
          v-bind="modelValue"
          @updated="updated"
          @confirmed="confirmed"
        />
      </template>
    </FilterItem>
  </div>
</template>

<script lang="ts" setup>
import { useDebounceFn } from '@vueuse/core'
import { diceCoefficient } from 'dice-coefficient'
import { MapPin, Tags, ListFilter, CalendarDays } from 'lucide-vue-next'
import IconChevronDown from '~/assets/icons/chevron-down.svg?component'
import IconBolt from '~/assets/icons/search-filters/bolt-stroke.svg?component'
import IconBoltFill from '~/assets/icons/search-filters/bolt-fill.svg?component'
import FilterItemOthers from '~/components/filter/FilterItemOthers.vue'
import FilterItemDateContent from '~/components/filter/FilterItemDateContent.vue'

import { useSearchEngineLocalities } from '~/composables/search/useSearchEngineLocalities'
import type { SearchFilterOptions } from '~/composables/useSearchFilters'
import type { SearchEngineLocation } from '~/composables/types/searchEngine'
import { localitySlugMatchesFilter } from '~/utils/localitySlugMatch'
import { useLocationTypeSlug } from '~/composables/filter/useLocationTypeSlug'

const emit = defineEmits(['filtersUpdated', 'searchUpdated'])
const { t } = useI18n()

const props = defineProps<{
  facets?: object
  filters?: SearchFilterOptions
}>()

const filterLocationSlugFromProps = computed(
  () => props.filters?.location?.toString() ?? ''
)

const { searchLocalities, setLocationSlug } = await useSearchEngineLocalities(
  filterLocationSlugFromProps,
  true
)

const categoriesFilterQuery = ref<string | undefined>(undefined)

const categories = computed(() => {
  return (
    Object.keys(props.facets?.['categories.name'] || {})
      .map((d) => ({
        slug: d,
        name: d,
        count: props?.facets?.['categories.name'][d],
        similarityScore: categoriesFilterQuery.value
          ? diceCoefficient(categoriesFilterQuery.value, d)
          : 1,
      }))
      .sort((a, b) => {
        if (categoriesFilterQuery.value) {
          return a.similarityScore > b.similarityScore ? -1 : 1
        } else {
          return a.name > b.name ? 1 : a.name < b.name ? -1 : 0
        }
      })
      // only show up to 10 categories if there is a query
      .slice(0, categoriesFilterQuery.value ? 10 : undefined)
  )
})

const selectedDates = computed(() =>
  // getting unique dates
  props.filters?.dates?.filter((x, i, arr) => arr.indexOf(x) === i)
)

const otherFilters = computed(() => ({
  typeOfClass: props.filters?.typeOfClass,
  hasAvailableDates: props.filters?.hasAvailableDates,
  languages: props.filters?.languages?.slice(0),
  priceMin: props.filters?.priceMin,
  priceMax: props.filters?.priceMax,
  directEvents: props.filters?.directEvents,
  publicEvents: props.filters?.publicEvents,
}))

const localities = computed(() => props?.facets?.location)

const { encodeLocationSlug, decodeLocationSlug, toLocationOptions } =
  useLocationTypeSlug()

const locationOptions = computed(() =>
  toLocationOptions(localities.value as SearchEngineLocation[] | undefined)
)

const onLocalityUpdated = (updatedVal: { location?: string }) => {
  const { slug, locationType } = decodeLocationSlug(updatedVal.location ?? '')
  setLocationSlug(slug)
  emit('filtersUpdated', {
    location: slug || undefined,
    locationType: slug ? locationType : undefined,
  })
}

const debouncedSearchLocalities = useDebounceFn((query: string) => {
  searchLocalities(query)
})

const debouncedSearchCategories = useDebounceFn((query: string) => {
  categoriesFilterQuery.value = query
}, 600)

const filterList = computed(() => [
  {
    icon: MapPin,
    iconActive: MapPin,
    iconClass: 'mr-1.5 w-5 h-5',
    arrowIcon: true,
    label: t('search.filters.location.label'),
    options: locationOptions.value.filter((l) => l.type !== 'admin_area'),
    dialogTitle: t('search.selectLocalityComponent.title'),
    filterKey: 'location',
    showSearch: true,
    selectedOptions: (() => {
      const slug = props.filters?.location
      if (!slug) return []
      const type = props.filters?.locationType ?? 'city'
      return [encodeLocationSlug(slug.toString(), type)]
    })(),
    getLabel: () =>
      (localities.value as SearchEngineLocation[] | undefined)?.find((l) =>
        localitySlugMatchesFilter(l.slug, props.filters?.location)
      )?.name ?? '',
    placeholder: t('search.selectLocalityComponent.searchPlaceholder'),
    isButtonHide: true,
    emits: {
      'search-updated': ($event) => {
        debouncedSearchLocalities($event)
      },
      'filters-updated': ($event) => {
        onLocalityUpdated($event)
      },
    },
  },
  {
    icon: Tags,
    iconActive: Tags, // You can replace this with a filled version of the icon
    iconClass: 'mr-1.5 w-5 h-5',
    arrowIcon: true,
    label: t('search.filters.category.chipLabel'),
    options: categories.value,
    dialogTitle: t('search.filters.category.label'),
    showSearch: true,
    selectedOptions: props.filters?.categories || [],
    multiSelect: true,
    placeholder: t('search.selectCategoryComponent.searchPlaceholder'),
    filterKey: 'categories',
    emits: {
      'search-updated': ($event) => {
        debouncedSearchCategories($event)
      },
      'filters-updated': ($event) => {
        emit('filtersUpdated', $event, 'categories')
      },
    },
  },
  {
    icon: CalendarDays,
    iconActive: CalendarDays,
    iconClass: 'mr-1.5 w-5 h-5',
    arrowIcon: true,
    label: t('search.filters.dates.label'),
    filterKey: 'dates',
    selectedOptions: { dates: selectedDates.value },
    multiSelect: true,
    contentComponent: FilterItemDateContent,
    emits: {
      'filters-updated': ($event) => {
        emit('filtersUpdated', $event)
      },
    },
  },
  {
    icon: IconBolt,
    iconActive: IconBoltFill,
    iconClass: 'mr-1.5 w-5 h-5',
    label: t('search.selectFiltersPromotions.label'),
    filterKey: 'hasPromotion',
    selectedOptions: [!!props.filters.hasPromotion],
    emits: {
      'filters-updated': ($event) => {
        emit('filtersUpdated', $event)
      },
    },
    contentComponent: FilterItemOthers,
    isToggle: true,
    customClasses:
      '[&>svg]:fill-[#FF6A39] hover:bg-[#FFFCF3] hover:border-red-700!',
    customSelectedClasses:
      '[&>svg]:fill-[#FF6A39] bg-[#FFFCF3] border-[#FFE1D4]! text-red-600',
  },
  {
    icon: ListFilter,
    iconActive: ListFilter, // You can replace this with a filled version of the icon
    iconClass: 'mr-1.5 w-4 h-4',
    arrowIcon: true,
    // dialogTitle: t('search.selectFiltersComponent.label'),
    label: t('search.selectFiltersComponent.label'),
    filterKey: 'other',
    selectedOptions: otherFilters?.value,
    emits: {
      'filters-updated': ($event) => {
        emit('filtersUpdated', $event)
      },
      'calendar-updated': ($event) => {
        emit('filtersUpdated', $event, 'dates')
      },
    },
    contentComponent: FilterItemOthers,
  },
])

// to detect if dropdown active in filters
const activeFilterItemId = ref(null)
provide('activeFilterItemId', activeFilterItemId)
</script>

<style lang="css" scoped>
@reference '~/assets/css/tailwind.css';
.filters-list {
  @apply flex gap-1 justify-start;
}
</style>
